/**
 * PassportIndex - Página de índice / sellos del pasaporte
 * Muestra hasta STAMPS_PER_PAGE sellos. Si hay más, se crean páginas adicionales.
 * Recibe solo el slice de países que le corresponde.
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import CountryIcon from "./CountryIcon";
import { hasPassportIcon, getStampColorForIcon } from "./passportIconUtils";
import Leather from "../../assets/passport/paper.png";

const stampInks = [
	{ main: "#b71c1c" },
	{ main: "#1565c0" },
	{ main: "#2e7d32" },
	{ main: "#6a1b9a" },
	{ main: "#e65100" },
	{ main: "#00838f" },
	{ main: "#4e342e" },
	{ main: "#c62828" },
];

const stampShapes = [
	"circle",
	"rectangle",
	"circle",
	"rectangle",
	"circle",
	"rectangle",
];

/** Códigos de país que siempre usan sello rectangular (nombre largo). */
const RECTANGLE_STAMP_COUNTRY_CODES = new Set(["US", "KR"]);

/** Longitud mínima del nombre del país para usar sello rectangular. */
const RECTANGLE_STAMP_NAME_LENGTH = 12;

function shouldUseRectangleStamp(country) {
	const name = (country.countryName || "").trim();
	if (
		RECTANGLE_STAMP_COUNTRY_CODES.has((country.countryCode || "").toUpperCase())
	)
		return true;
	return name.length >= RECTANGLE_STAMP_NAME_LENGTH;
}

function getShapeForCountry(country, globalIndex) {
	if (shouldUseRectangleStamp(country)) return "rectangle";
	return stampShapes[globalIndex % stampShapes.length];
}

function getInkForCountry(country, globalIndex) {
	if (hasPassportIcon(country.countryCode, country.countryName)) {
		return { main: getStampColorForIcon(country.countryCode) };
	}
	return stampInks[globalIndex % stampInks.length];
}

const StampCircle = ({ country, ink, rotation, onClick }) => {
	const displayName = (country.countryName || "").toUpperCase();
	const year = country.visitDate
		? new Date(country.visitDate + "T00:00:00").getFullYear()
		: "";

	return (
		<Box
			onClick={onClick}
			sx={{
				width: { xs: 95, sm: 110 },
				height: { xs: 95, sm: 110 },
				borderRadius: "50%",
				border: `2.5px solid ${ink.main}`,
				display: "flex",
				flexDirection: "column",
				cursor: "pointer",
				transform: `rotate(${rotation}deg)`,
				transition: "all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
				position: "relative",
				opacity: 0.82,
				alignItems: "center",
				justifyContent: "center",
				"&:hover": {
					transform: "rotate(0deg) scale(1.1)",
					opacity: 1,
					boxShadow: `0 6px 24px ${ink.main}30`,
				},
				"&::before": {
					content: '""',
					position: "absolute",
					inset: 5,
					borderRadius: "50%",
					border: `1.5px dashed ${ink.main}`,
					opacity: 0.5,
				},
				"&::after": {
					content: '""',
					position: "absolute",
					inset: -3,
					borderRadius: "50%",
					border: `1px solid ${ink.main}`,
					opacity: 0.2,
				},
			}}
		>
			<Typography
				sx={{
					fontSize: "0.33rem",
					color: ink.main,
					fontWeight: 700,
					textTransform: "uppercase",
					letterSpacing: "0.2em",
					opacity: 0.5,
					mb: 0.15,
				}}
			>
				{country.countryCode}
			</Typography>
			<Box
				sx={{
					mb: 0.1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<CountryIcon
					countryCode={country.countryCode}
					countryName={country.countryName}
					size="md"
					imgSx={{ width: { xs: 30, sm: 40 } }}
					sx={{ fontSize: { xs: "1.4rem", sm: "1.6rem" } }}
				/>
			</Box>
			<Typography
				sx={{
					fontSize: { xs: "0.42rem", sm: "0.48rem" },
					color: ink.main,
					fontWeight: 800,
					textTransform: "uppercase",
					letterSpacing: "0.06em",
					textAlign: "center",
					px: 1,
					lineHeight: 1.15,
					maxWidth: "85%",
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}
			>
				{displayName}
			</Typography>
			{year && (
				<Typography
					sx={{
						fontSize: "0.38rem",
						color: ink.main,
						opacity: 0.6,
						mt: 0.15,
						fontWeight: 600,
					}}
				>
					{year}
				</Typography>
			)}
		</Box>
	);
};

const StampRectangle = ({ country, ink, rotation, onClick }) => {
	const displayName = (country.countryName || "").toUpperCase();
	const dateStr = country.visitDate
		? new Date(country.visitDate + "T00:00:00").toLocaleDateString("es-ES", {
				month: "short",
				year: "numeric",
		  })
		: "";

	return (
		<Box
			onClick={onClick}
			sx={{
				width: { xs: 105, sm: 120 },
				height: { xs: 78, sm: 88 },
				borderRadius: "4px",
				border: `2.5px solid ${ink.main}`,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				pt: 0.5,
				cursor: "pointer",
				transform: `rotate(${rotation}deg)`,
				transition: "all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
				position: "relative",
				opacity: 0.82,
				"&:hover": {
					transform: "rotate(0deg) scale(1.1)",
					opacity: 1,
					boxShadow: `0 6px 24px ${ink.main}30`,
				},
				"&::before": {
					content: '""',
					position: "absolute",
					inset: 4,
					borderRadius: "2px",
					border: `1.5px dashed ${ink.main}`,
					opacity: 0.4,
				},
			}}
		>
			<Typography
				sx={{
					fontSize: "0.33rem",
					color: ink.main,
					fontWeight: 700,
					textTransform: "uppercase",
					letterSpacing: "0.2em",
					opacity: 0.5,
					mb: 0.15,
				}}
			>
				{country.countryCode}
			</Typography>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					mt: 0.35,
					mb: 0.1,
				}}
			>
				<CountryIcon
					countryCode={country.countryCode}
					countryName={country.countryName}
					size="sm"
					imgSx={{ width: { xs: 30, sm: 40 } }}
					sx={{ fontSize: { xs: "1rem", sm: "1.2rem" } }}
				/>
			</Box>
			<Typography
				sx={{
					fontSize: { xs: "0.4rem", sm: "0.45rem" },
					color: ink.main,
					fontWeight: 800,
					textTransform: "uppercase",
					letterSpacing: "0.05em",
					textAlign: "center",
					px: 1,
					mt: 0.1,
					lineHeight: 1.15,
					maxWidth: "90%",
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}
			>
				{displayName}
			</Typography>
			{dateStr && (
				<Typography
					sx={{
						fontSize: "0.33rem",
						color: ink.main,
						opacity: 0.5,
						mt: 0.15,
						fontWeight: 600,
						textTransform: "uppercase",
					}}
				>
					{dateStr}
				</Typography>
			)}
		</Box>
	);
};

/**
 * Props:
 * - countries: slice de países a mostrar en esta página
 * - globalOffset: offset global del primer país en este slice (para colores/rotaciones consistentes)
 * - totalCountries: total de países en el pasaporte
 * - currentIndexPage: 0-based index de esta página de sellos
 * - totalIndexPages: cuántas páginas de sellos hay en total
 * - isLastIndexPage: si es la última página de índice (muestra botón añadir)
 * - onNavigate(globalIndex): callback con el índice global del país
 * - onAddCountry: callback para abrir el diálogo
 */
const PassportIndex = ({
	countries,
	globalOffset,
	totalCountries,
	currentIndexPage,
	totalIndexPages,
	isLastIndexPage,
	onNavigate,
	onAddCountry,
	readOnly = false,
}) => {
	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				background:
					"linear-gradient(175deg, #f7f3ec 0%, #f0ebe0 50%, #ede6d4 100%)",
				display: "flex",
				flexDirection: "column",
				position: "relative",
				overflow: "hidden",
				fontFamily: "'Playfair Display', serif",
			}}
		>
			{/* Fondo textura papel */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					backgroundImage: `url(${Leather})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
					opacity: 0.5,
					pointerEvents: "none",
				}}
			/>
			{/* Textura papel vintage (capa suave extra) */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					opacity: 0.25,
					backgroundImage: `
						radial-gradient(ellipse at 20% 50%, rgba(200,185,160,0.15) 0%, transparent 50%),
						radial-gradient(ellipse at 80% 30%, rgba(200,185,160,0.1) 0%, transparent 50%),
						radial-gradient(ellipse at 50% 80%, rgba(200,185,160,0.12) 0%, transparent 50%)
					`,
					pointerEvents: "none",
				}}
			/>

			{/* Header */}
			<Box
				sx={{
					pt: { xs: 2, sm: 2.5 },
					pb: { xs: 1, sm: 1.5 },
					px: 3,
					textAlign: "center",
					position: "relative",
					flexShrink: 0,
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 1.5,
						mb: 0.75,
					}}
				>
					<Box
						sx={{
							flex: 1,
							maxWidth: 60,
							height: "1px",
							background:
								"linear-gradient(90deg, transparent, rgba(0,0,0,0.12))",
						}}
					/>
					<FlightTakeoffIcon
						sx={{
							fontSize: 14,
							color: "rgba(0,0,0,0.15)",
							transform: "rotate(-10deg)",
						}}
					/>
					<Box
						sx={{
							flex: 1,
							maxWidth: 60,
							height: "1px",
							background:
								"linear-gradient(90deg, rgba(0,0,0,0.12), transparent)",
						}}
					/>
				</Box>

				<Typography
					sx={{
						fontSize: "0.5rem",
						color: "rgba(0,0,0,0.28)",
						textTransform: "uppercase",
						letterSpacing: "0.3em",
						mb: 0.25,
						fontWeight: 500,
					}}
				>
					Registro de viajes
				</Typography>
				<Typography
					sx={{
						fontSize: { xs: "1.2rem", sm: "1.4rem" },
						color: "#2c2c2c",
						fontWeight: 700,
						letterSpacing: "0.02em",
					}}
				>
					Mis Sellos
				</Typography>

				<Box
					sx={{
						display: "inline-flex",
						alignItems: "center",
						gap: 0.75,
						mt: 0.5,
						px: 2,
						py: 0.3,
						borderRadius: "20px",
						backgroundColor: "rgba(0,0,0,0.04)",
						border: "1px solid rgba(0,0,0,0.06)",
					}}
				>
					<FlightTakeoffIcon
						sx={{ fontSize: "0.7rem", color: "rgba(0,0,0,0.4)" }}
					/>
					<Typography
						sx={{
							fontSize: "0.55rem",
							color: "rgba(0,0,0,0.4)",
							fontWeight: 500,
						}}
					>
						{totalCountries === 0
							? "¡Añade tu primer destino!"
							: `${totalCountries} destino${totalCountries > 1 ? "s" : ""}`}
					</Typography>
				</Box>
			</Box>

			{/* Separador */}
			<Box
				sx={{
					mx: 3,
					height: "1px",
					flexShrink: 0,
					background:
						"linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.08) 20%, rgba(0,0,0,0.08) 80%, transparent 100%)",
				}}
			/>

			{/* Sellos — centrados verticalmente cuando la página está llena (12 sellos) */}
			<Box
				sx={{
					flex: 1,
					overflow: "auto",
					px: { xs: 1.5, sm: 2 },
					py: 1.5,
					display: "flex",
					flexWrap: "wrap",
					gap: { xs: 2.5, sm: 3 },
					alignContent: "center",
					justifyContent: "center",
					"&::-webkit-scrollbar": { display: "none" },
				}}
			>
				{countries.map((country, localIndex) => {
					const globalIndex = globalOffset + localIndex;
					const ink = getInkForCountry(country, globalIndex);
					const shape = getShapeForCountry(country, globalIndex);
					const rotationsCircle = [-8, 5, -3, 12, -6, 9, -11, 4, -2, 7];
					const rotationsRect = [-3, 2, -2, 3, -2, 2, -3, 2, -2, 2];
					const rotation =
						shape === "rectangle"
							? rotationsRect[globalIndex % rotationsRect.length]
							: rotationsCircle[globalIndex % rotationsCircle.length];
					const key = country.countryCode || country.visits?.[0]?.id || globalIndex;

					if (shape === "rectangle") {
						return (
							<StampRectangle
								key={key}
								country={country}
								ink={ink}
								rotation={rotation}
								onClick={() => onNavigate(globalIndex)}
							/>
						);
					}

					return (
						<StampCircle
							key={key}
							country={country}
							ink={ink}
							rotation={rotation}
							onClick={() => onNavigate(globalIndex)}
						/>
					);
				})}

				{/* Botón añadir: solo en la última página de índice */}
				{isLastIndexPage && !readOnly && onAddCountry && (
					<Box
						onClick={onAddCountry}
						sx={{
							width: { xs: 95, sm: 110 },
							height: { xs: 95, sm: 110 },
							borderRadius: "50%",
							border: "2.5px dashed rgba(0,0,0,0.1)",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
							transition: "all 0.3s ease",
							"&:hover": {
								borderColor: "rgba(201, 168, 76, 0.6)",
								backgroundColor: "rgba(201, 168, 76, 0.06)",
								transform: "scale(1.05)",
								"& .add-icon-wrapper": {
									backgroundColor: "rgba(201, 168, 76, 0.15)",
								},
							},
						}}
					>
						<Box
							className="add-icon-wrapper"
							sx={{
								width: 32,
								height: 32,
								borderRadius: "50%",
								backgroundColor: "rgba(0,0,0,0.04)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								mb: 0.5,
								transition: "all 0.3s ease",
							}}
						>
							<AddIcon sx={{ fontSize: 18, color: "rgba(0,0,0,0.25)" }} />
						</Box>
						<Typography
							sx={{
								fontSize: "0.48rem",
								color: "rgba(0,0,0,0.25)",
								fontWeight: 600,
								textTransform: "uppercase",
								letterSpacing: "0.08em",
							}}
						>
							Añadir
						</Typography>
					</Box>
				)}
			</Box>

			{/* Footer */}
			<Box
				sx={{
					py: 1,
					textAlign: "center",
					borderTop: "1px solid rgba(0,0,0,0.04)",
					backgroundColor: "rgba(255,255,255,0.3)",
					flexShrink: 0,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					gap: 1.5,
				}}
			>
				{totalIndexPages > 1 && (
					<Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
						{Array.from({ length: totalIndexPages }).map((_, i) => (
							<Box
								key={i}
								sx={{
									width: i === currentIndexPage ? 12 : 5,
									height: 5,
									borderRadius: 3,
									backgroundColor:
										i === currentIndexPage
											? "rgba(0,0,0,0.25)"
											: "rgba(0,0,0,0.08)",
									transition: "all 0.2s ease",
								}}
							/>
						))}
					</Box>
				)}
				<Typography
					sx={{
						fontSize: "0.42rem",
						color: "rgba(0,0,0,0.18)",
						letterSpacing: "0.1em",
						textTransform: "uppercase",
					}}
				>
					Toca un sello para ver los detalles
				</Typography>
			</Box>
		</Box>
	);
};

export default PassportIndex;
